import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import SvgLocationPinFill from '@common/icon/SvgLocationPinFill';
import SvgStarFill from '@common/icon/SvgStarFill';
import SvgVisible from '@common/icon/SvgVisible';
import { cn } from '@core/utils';
import type { YearEndGoodsInstitution } from '@/api/getYearEndGoodsInstitutionList';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { BREAKPOINTS, DONATION_ROUTES } from '@/constants';
import { GOODS_HOME_IMAGE_URL } from '@/constants/yearEndDonationHome';
import { FixedImage } from '../FixedImage';
import { getGoodsStockPill, type GoodsGiftCardItem } from './goodsGiftCards';
const STOCK_VALUE_TONE_CLASS_NAME = {
  normal: 'text-G-300',
  low: 'text-R-300',
  soldOut: 'text-Gray-400'
} as const;
const GoodsStockPill = t0 => {
  const $ = _c(19);
  const {
    institution
  } = t0;
  let t1;
  if ($[0] !== institution) {
    t1 = getGoodsStockPill(institution);
    $[0] = institution;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    label,
    value,
    tone
  } = t1;
  const isSoldOut = tone === "soldOut";
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex w-fit items-center gap-8 rounded-small border border-Gray-white/30 bg-Gray-white/15 py-4 pl-4 pr-12 backdrop-blur-sm", "mobile-tablet:gap-4 mobile-tablet:rounded-xsmall mobile-tablet:pr-8");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isSoldOut ? GOODS_HOME_IMAGE_URL.BOOK_SOLD_OUT : GOODS_HOME_IMAGE_URL.BOOK;
  let t4;
  if ($[3] !== t3) {
    t4 = <span className="relative size-28 shrink-0 overflow-hidden rounded-xsmall bg-Gray-white mobile-tablet:size-20 mobile-tablet:rounded-xxsmall"><FixedImage src={t3} sizes="28px" className="absolute inset-0" imageClassName="object-contain" /></span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isSoldOut && "opacity-60";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("whitespace-nowrap font-500 text-Gray-white/85 typo-body4 mobile-tablet:typo-caption2", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== label || $[8] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[7] = label;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = STOCK_VALUE_TONE_CLASS_NAME[tone];
  let t9;
  if ($[10] !== t8) {
    t9 = cn("whitespace-nowrap font-700 typo-body4 mobile-tablet:typo-caption1", t8);
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t9 || $[13] !== value) {
    t10 = <span className={t9}>{value}</span>;
    $[12] = t9;
    $[13] = value;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t4 || $[17] !== t7) {
    t11 = <span className={t2}>{t4}{t7}{t10}</span>;
    $[15] = t10;
    $[16] = t4;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};
const ViewerChip = t0 => {
  const $ = _c(8);
  const {
    readCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SvgVisible className="size-14 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== readCount) {
    t2 = readCount.toLocaleString();
    $[1] = readCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `${t2}명`;
  let t4;
  if ($[3] !== t3) {
    t4 = <span>{t3}</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span>이 보는 중</span>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4) {
    t6 = <span className="flex shrink-0 items-center gap-4 rounded-pill bg-Gray-black/55 px-8 py-4 text-Gray-white backdrop-blur-sm">{t1}<span className="whitespace-nowrap font-600 typo-caption1">{t4}{t5}</span></span>;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
};
export interface GoodsGiftCardProps {
  institution: YearEndGoodsInstitution;
  gift: Pick<GoodsGiftCardItem['gift'], 'institutionId' | 'institutionName' | 'productNumber' | 'productName' | 'mainImageUrl' | 'requiredDonationAmount' | 'reviewCount' | 'reviewRating'>;
  readCount?: number;
}
export const GoodsGiftCard = t0 => {
  const $ = _c(43);
  const {
    institution,
    gift,
    readCount
  } = t0;
  let t1;
  if ($[0] !== gift.productNumber) {
    t1 = DONATION_ROUTES.GIFTS_DETAIL.pathname({
      productNumber: gift.productNumber
    });
    $[0] = gift.productNumber;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("group relative flex h-[25rem] w-288 shrink-0 flex-col justify-end overflow-hidden rounded-medium bg-Gray-100 p-16", "mobile-tablet:h-244 mobile-tablet:w-168 mobile-tablet:p-12", FOCUS_RING);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== gift.mainImageUrl) {
    t3 = <Image fill={true} src={gift.mainImageUrl} alt="" sizes={`${BREAKPOINTS.MOBILE_TABLET} 168px, 288px`} className="object-cover transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-70 motion-reduce:transition-none" />;
    $[3] = gift.mainImageUrl;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-transparent from-35% to-Gray-black/90" />;
    t5 = <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-medium border border-Gray-black/5" />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SvgLocationPinFill className="size-12 shrink-0 text-B-500" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== gift.institutionName) {
    t7 = <span className="flex min-w-0 items-center gap-4 rounded-pill bg-Gray-white px-8 py-4">{t6}<span className="truncate font-600 text-Gray-900 typo-caption1">{gift.institutionName}</span></span>;
    $[8] = gift.institutionName;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== readCount) {
    t8 = readCount ? <ViewerChip readCount={readCount} /> : null;
    $[10] = readCount;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <span className="absolute inset-x-16 top-16 flex items-center justify-between gap-4 mobile-tablet:hidden">{t7}{t8}</span>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== readCount) {
    t10 = readCount ? <><span className="font-200 text-Gray-white/30"> | </span>{`${readCount.toLocaleString()}명이 보는중`}</> : null;
    $[15] = readCount;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== gift.institutionName || $[18] !== t10) {
    t11 = <span className="absolute left-8 top-8 hidden max-w-[calc(100%-1rem)] rounded-pill bg-Gray-black/50 p-8 text-Gray-white backdrop-blur-sm mobile-tablet:flex"><span className="truncate whitespace-nowrap text-[0.625rem] font-500 leading-[1.2]">{gift.institutionName}{t10}</span></span>;
    $[17] = gift.institutionName;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SvgStarFill className="size-12 text-Y-550" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== gift.reviewRating) {
    t13 = gift.reviewRating.toFixed(1);
    $[21] = gift.reviewRating;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== gift.reviewCount) {
    t14 = gift.reviewCount.toLocaleString();
    $[23] = gift.reviewCount;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const t15 = `${t13} (${t14})`;
  let t16;
  if ($[25] !== t15) {
    t16 = <span className="flex items-center gap-4 font-400 text-Gray-white typo-caption1 mobile-tablet:font-600 mobile-tablet:text-Gray-white/90">{t12}{t15}</span>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== gift.productName) {
    t17 = <span className="truncate font-600 typo-body3 mobile-tablet:line-clamp-2 mobile-tablet:min-h-[3em] mobile-tablet:whitespace-normal mobile-tablet:typo-body4">{gift.productName}</span>;
    $[27] = gift.productName;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t16 || $[30] !== t17) {
    t18 = <span className="flex flex-col gap-4">{t16}{t17}</span>;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== institution) {
    t19 = <GoodsStockPill institution={institution} />;
    $[32] = institution;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <span className="relative flex w-full flex-col gap-8 text-Gray-white">{t18}{t19}</span>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t1 || $[38] !== t11 || $[39] !== t20 || $[40] !== t3 || $[41] !== t9) {
    t21 = <CustomLink prefetch={false} href={t1} className={t2}>{t3}{t4}{t5}{t9}{t11}{t20}</CustomLink>;
    $[37] = t1;
    $[38] = t11;
    $[39] = t20;
    $[40] = t3;
    $[41] = t9;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};